import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import useFormHook from '@shared/hooks/useFormHook';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { personService } from '@shared/services/personService';
import { currencyService } from '@modules/payments/currency/services/currency';
import { useNavigate } from 'react-router-dom';
import useIbanValidation from "@shared/hooks/useIbanValidation";

export default function BankAccountsForm({ onClose, onConfirm, editData, formProps }) {
    const { modalRef } = useFormHook({}, onClose);
    const [banks, setBanks] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const validationSchema = yup.object().shape({
        bankId: yup.string().required(t('common.required')),
        currencyId: yup.string().required(t('common.required')),
        iban: yup.string()
            .required(t('common.required')),
        purpose: yup.string().required(t('common.required')),
    });

    const { register, watch, setValue, handleSubmit, reset, formState: { errors, setError } } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        criteriaMode: 'all',
        defaultValues: {
            bankId: '',
            bankCode: '',
            bankName: '',
            currencyId: '',
            currencyMnemonic: '',
            currencyDescription: '',
            iban: '',
            purpose: '',
            active: true,
            account: '',
            paymentsInFolder: ''
        }
    });

    useEffect(() => {
        if (editData && banks.length > 0 && currencies.length > 0) {
            // Set bank values
            const selectedBank = banks.find(b => String(b.branchId) === String(editData.banks.bankId));
            if (selectedBank) {
                setValue('bankId', selectedBank.branchId);
                setValue('bankCode', selectedBank.bankCode);
                setValue('bankName', selectedBank.name);
            }

            // Set currency values
            const selectedCurrency = currencies.find(c => c.mnemonic === editData.currency.mnemonic);
            if (selectedCurrency) {
                setValue('currencyId', selectedCurrency.codeIso);
                setValue('currencyMnemonic', selectedCurrency.mnemonic);
                setValue('currencyDescription', selectedCurrency.description);
            } 
            setValue('iban', editData.iban);
            setValue('purpose', editData.note);
            setValue('active', editData.active);
            setValue('account', editData.accounts);
            setValue('paymentsInFolder', editData.paymentsInFolder);

        }
    }, [editData, banks, currencies, setValue]);

    const fetchBanks = async () => {
        setLoading(true);
        try {
            const banksData = await personService.banksSelect();
            setBanks(banksData);
        } catch (error) {
            console.error('Error fetching banks:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCurrencies = async () => {
        setLoading(true);
        try {
            const currenciesData = await currencyService.getCurrencies();
            setCurrencies(currenciesData);
        } catch (error) {
            console.error('Error fetching currencies:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBanks();
        fetchCurrencies();
    }, []);

    const handleBankSelect = (e) => {
        const selectedBank = banks.find(b => String(b.branchId) === e.target.value);
        if (selectedBank) {
            setValue('bankId', selectedBank.branchId);
            setValue('bankCode', selectedBank.bankCode);
            setValue('bankName', selectedBank.name);
        } else {
            setValue('bankId', '');
            setValue('bankCode', '');
            setValue('bankName', '');
        }
    };

    const handleCurrencySelect = (e) => {
        const selectedCurrency = currencies.find(c => String(c.codeIso) === e.target.value);
        if (selectedCurrency) {
            setValue('currencyId', selectedCurrency.codeIso);
            setValue('currencyMnemonic', selectedCurrency.mnemonic);
            setValue('currencyDescription', selectedCurrency.description);
        } else {
            setValue('currencyId', '');
            setValue('currencyMnemonic', '');
            setValue('currencyDescription', '');
        }
    };

    const submitForm = async (data) => {
        if (onConfirm) {
            try {
                await onConfirm({
                    ...data,
                    bankId: data.bankId,
                    currencyId: data.currencyId,
                    iban: data.iban,
                    active: data.active === true,
                    purpose: data.purpose || ''
                });
            } catch (error) {
                if (error.message?.includes('duplicate key value violates unique constraint "firm_bank_acc_branch_id_iban_key"')) {
                    setError('iban', {
                        type: 'manual',
                        message: t('validation.bankAccounts.ibanExists')
                    });
                } else {
                    console.error('Error submitting form:', error);
                }
            }
        }
    }

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {editData ? t('common.edit') : t('common.add')} {t('payments.bankAccounts.title')}
                        </h4>
                        <form id="bankAccountsForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                            <div className="flex-column">
                                <div className="flex-row gap-10">
                                    <div className="form-group width-50">
                                        <h3 className="title-nowrap"> {t('payments.bankAccounts.form.bankBranch')}: </h3>
                                        <span className="span-bold"> {t('payments.bankAccounts.form.code')}:  <span className="required">*</span></span>
                                        <select
                                            name="bankId"
                                            className={`form-control ${errors.bankId ? 'is-invalid' : ''}`}
                                            {...register('bankId')}
                                            onChange={handleBankSelect}
                                            disabled={loading}
                                            value={watch('bankId') || ''}
                                        >
                                            <option value="">{loading ? 'Loading...' : t('persons.banks.selectBank')}</option>
                                            {banks.map(bank => (
                                                <option key={bank.branchId} value={bank.branchId}>
                                                    {bank.name} ({bank.bankCode})
                                                </option>
                                            ))}
                                        </select>
                                        {errors.bankId && <p className="text-danger">{errors.bankId.message}</p>}
                                        <span className="span-bold"> {t('payments.bankAccounts.form.bank')}:  <span className="required">*</span></span>
                                        <input type="text" className="form-control" {...register('bankCode')} />
                                        <span className="span-bold"> {t('payments.bankAccounts.form.purpose')}:  <span className="required">*</span></span>
                                        <input type="text" className="form-control" {...register('purpose')} />
                                        {errors.purpose && <p className="text-danger">{errors.purpose.message}</p>}
                                    </div>
                                    <div className="form-group width-50">
                                        <h3 className="title-nowrap"> {t('payments.bankAccounts.form.bankAccount')}: </h3>
                                        <span className="span-bold"> {t('payments.bankAccounts.form.iban')}:  <span className="required">*</span></span>
                                        <input
                                            type="text"
                                            placeholder="IBAN"
                                            className={`form-control ${errors.iban ? 'is-invalid' : ''}`}
                                            {...register('iban')}
                                            // maxLength={22}
                                        />
                                        {errors.iban && <p className="text-danger">{errors.iban.message}</p>}
                                        <span className="span-bold"> {t('payments.bankAccounts.form.currency')}:  <span className="required">*</span></span>
                                        <select
                                            name="currencyId"
                                            className={`form-control ${errors.currencyId ? 'is-invalid' : ''}`}
                                            {...register('currencyId')}
                                            onChange={handleCurrencySelect}
                                            disabled={loading}
                                            value={watch('currencyId') || ''}
                                        >
                                            <option value="">{loading ? 'Loading...' : t('common.select')}</option>
                                            {currencies.map(currency => (
                                                <option key={currency.codeIso} value={currency.codeIso}>
                                                    {currency.codeIso} - {currency.description}
                                                </option>
                                            ))}
                                        </select>
                                        <button type="button" className="form-btn btn-green" onClick={() => navigate(`/payments/settings/currencies`)}>
                                            {t('common.viewCurrencies')}
                                        </button>
                                        {errors.currencyId && <p className="text-danger">{errors.currencyId.message}</p>}
                                        <span className="span-bold"> {t('payments.currencies.form.active')}:</span>
                                        <div className="checkbox-wrapper-start title-checkbox">
                                            <input
                                                type="checkbox"
                                                id="active"
                                                {...register('active')}
                                            />
                                            <label htmlFor="active"> {t('payments.currencies.form.active')}</label>
                                        </div>
                                    
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <button type="submit" form="bankAccountsForm" className="btn btn-primary">
                            {t('common.save')}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
